import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Devices as DevicesIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  SignalWifi4Bar as SignalIcon,
  SignalWifiOff as SignalOffIcon,
  Battery90 as BatteryIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { Device } from '../../types';
import { formatDate, formatTimeAgo, getStatusColor, getStatusLabel, calculateDeviceHealth, getDeviceDisplayName, getDeviceSubtitle } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface DeviceRowProps {
  device: Device;
  onEdit: (device: Device) => void;
  onView: (device: Device) => void;
  onViewMap: (device: Device) => void;
  onUpdateStatus: (device: Device, status: Device['status']) => void;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ device, onEdit, onView, onViewMap, onUpdateStatus }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#8BC34A';
      case 'fair': return '#FF9800';
      case 'poor': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getHealthLabel = (health: string) => {
    switch (health) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Bueno';
      case 'fair': return 'Regular';
      case 'poor': return 'Malo';
      default: return 'Desconocido';
    }
  };

  const deviceHealth = calculateDeviceHealth(device);
  const isOnline = device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000);

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: getStatusColor(device.status) }}>
            <DevicesIcon />
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {getDeviceDisplayName(device)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getDeviceSubtitle(device) || `IMEI: ${device.imei}`}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {(typeof device.clientId === 'object' && device.clientId !== null) ? device.clientId.name : 'Sin asignar'}
        </Typography>
        {(typeof device.vehicleId === 'object' && device.vehicleId !== null && device.vehicleId?.plate) && (
          <Typography variant="body2" color="text.secondary">
            Placa: {(typeof device.vehicleId === 'object' && device.vehicleId !== null) ? device.vehicleId.plate : ''}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={getStatusLabel(device.status)}
            sx={{
              backgroundColor: getStatusColor(device.status) + '20',
              color: getStatusColor(device.status),
              border: `1px solid ${getStatusColor(device.status)}`,
            }}
            size="small"
          />
          {isOnline ? (
            <Tooltip title="En línea">
              <SignalIcon sx={{ color: 'success.main', fontSize: 20 }} />
            </Tooltip>
          ) : (
            <Tooltip title="Desconectado">
              <SignalOffIcon sx={{ color: 'error.main', fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {(() => {
          const expTime = (device as any).expirationTime;
          if (!expTime) return <Typography variant="body2" color="text.secondary">Sin fecha</Typography>;
          const expDate = new Date(expTime);
          const now = new Date();
          const days = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          const isExpired = days < 0;
          const isSoon = days >= 0 && days <= 30;
          return (
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: (isExpired || isSoon) ? 600 : 400 }}
                color={isExpired ? 'error' : isSoon ? 'warning.main' : 'text.primary'}
              >
                {isExpired ? `Expirado (${Math.abs(days)}d)` : isSoon ? `${days}d restantes` : formatDate(expTime)}
              </Typography>
            </Box>
          );
        })()}
      </TableCell>
      <TableCell>
        {device.lastSeen ? (
          <Typography variant="body2" color="text.secondary">
            {formatTimeAgo(device.lastSeen)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">Sin datos</Typography>
        )}
      </TableCell>
      <TableCell>
        {(typeof (device as any).simCardId === 'object' && (device as any).simCardId?.iccid) ? (
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {(device as any).simCardId.iccid.slice(-8)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { onView(device); handleMenuClose(); }}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => { onEdit(device); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Editar
          </MenuItem>
          {device.lastPosition && (
            <MenuItem onClick={() => { onViewMap(device); handleMenuClose(); }}>
              <MapIcon sx={{ mr: 1 }} fontSize="small" />
              Ver en Mapa
            </MenuItem>
          )}
          <MenuItem onClick={() => { onUpdateStatus(device, device.status === 'active' ? 'inactive' : 'active'); handleMenuClose(); }}>
            <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
            {device.status === 'active' ? 'Desactivar' : 'Activar'}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export const DevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const {
    data: devicesData,
    loading,
    error,
    execute: fetchDevices,
  } = useApi<{ devices: Device[]; total: number; globalTotal: number; pages: number }>(
    () => apiService.getDevices({ page: page + 1, limit: rowsPerPage, search: searchTerm || undefined }),
    { immediate: false }
  );

  const { execute: updateStatus, loading: updating } = useAsyncOperation();

  const {
    data: deviceStats,
    loading: statsLoading,
    execute: fetchStats,
  } = useApi<any>(() => apiService.getDeviceStats(), { immediate: false });

  useEffect(() => {
    fetchDevices();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, fetchDevices, fetchStats]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (device: Device) => {
    navigate(`/devices/${device._id}`);
  };

  const handleEdit = (device: Device) => {
    navigate(`/devices/${device._id}/edit`);
  };

  const handleViewMap = (device: Device) => {
    navigate(`/live-map?device=${device._id}`);
  };

  const handleUpdateStatus = async (device: Device, status: Device['status']) => {
    const result = await updateStatus(() => apiService.updateDeviceStatus(device._id, status));
    if (result) {
      fetchDevices();
      fetchStats(); // Refresh stats after status update
    }
  };

  const handleAddDevice = () => {
    navigate('/devices/new');
  };


  const getFilteredDevices = () => {
    if (!devicesData?.devices) return [];
    if (statusFilter === 'all') return devicesData.devices;
    return devicesData.devices.filter(device => device.status === statusFilter);
  };

  const getStatusCounts = () => {
    console.log('Debug device stats:', {
      deviceStats: deviceStats?.data,
      devicesGlobalTotal: devicesData?.globalTotal,
      statsLoading,
      loading
    });

    // Always use global stats from backend, fallback to 0 if not available
    if (deviceStats?.data) {
      return {
        total: deviceStats.data.total || 0,
        active: deviceStats.data.byStatus?.active || 0,
        inactive: deviceStats.data.byStatus?.inactive || 0,
        maintenance: deviceStats.data.byStatus?.maintenance || 0,
      };
    }

    // If no backend stats available, use global total from devices endpoint
    if (devicesData?.globalTotal !== undefined) {
      return {
        total: devicesData.globalTotal || 0,
        active: 0, // We don't have status breakdown without stats endpoint
        inactive: 0,
        maintenance: 0,
      };
    }

    // Last fallback to zeros
    return { total: 0, active: 0, inactive: 0, maintenance: 0 };
  };

  const statusCounts = getStatusCounts();
  const filteredDevices = getFilteredDevices();

  if (error) {
    return (
      <Alert severity="error">
        Error cargando dispositivos: {error}
        <Button onClick={() => fetchDevices()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  // Componente de tarjeta para vista móvil
  const DeviceCard: React.FC<{ device: Device }> = ({ device }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isOnline = device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000);

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar sx={{ bgcolor: getStatusColor(device.status) }}>
                <DevicesIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }} noWrap>
                  {getDeviceDisplayName(device)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getDeviceSubtitle(device) || `IMEI: ${device.imei}`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { handleView(device); setAnchorEl(null); }}>
                <ViewIcon sx={{ mr: 1 }} fontSize="small" /> Ver Detalles
              </MenuItem>
              <MenuItem onClick={() => { handleEdit(device); setAnchorEl(null); }}>
                <EditIcon sx={{ mr: 1 }} fontSize="small" /> Editar
              </MenuItem>
              {device.traccarId && (
                <MenuItem onClick={() => { handleViewMap(device); setAnchorEl(null); }}>
                  <MapIcon sx={{ mr: 1 }} fontSize="small" /> Ver en Mapa
                </MenuItem>
              )}
            </Menu>
          </Box>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Cliente:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {(typeof device.clientId === 'object' && device.clientId !== null) ? device.clientId.name : 'Sin asignar'}
              </Typography>
            </Box>
            {device.lastSeen && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Última conexión:</Typography>
                <Typography variant="body2">{formatTimeAgo(device.lastSeen)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={getStatusLabel(device.status)}
                sx={{
                  backgroundColor: getStatusColor(device.status) + '20',
                  color: getStatusColor(device.status),
                  border: `1px solid ${getStatusColor(device.status)}`,
                }}
                size="small"
              />
              {isOnline ? (
                <Chip icon={<SignalIcon />} label="En línea" color="success" size="small" variant="outlined" />
              ) : (
                <Chip icon={<SignalOffIcon />} label="Desconectado" color="error" size="small" variant="outlined" />
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: { xs: 2, md: 4 }
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Dispositivos GPS
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Gestiona todos los dispositivos GPS del sistema
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchDevices()}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDevice}
            size="large"
          >
            Nuevo Dispositivo
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {statusCounts.total}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total Dispositivos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {statusCounts.active}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Activos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 600 }}>
                {statusCounts.inactive}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Inactivos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {statusCounts.maintenance}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Mantenimiento
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por IMEI, marca o cliente..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 300 } }}
            size={isMobile ? 'small' : 'medium'}
          />
          <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size={isMobile ? 'small' : 'medium'}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Estado"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="inactive">Inactivos</MenuItem>
              <MenuItem value="maintenance">Mantenimiento</MenuItem>
              <MenuItem value="lost">Perdidos</MenuItem>
              <MenuItem value="stolen">Robados</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Devices Table / Cards */}
      {isMobile ? (
        // Vista móvil con cards
        <Box>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !filteredDevices.length ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm || statusFilter !== 'all' ? 'No se encontraron dispositivos' : 'No hay dispositivos registrados'}
              </Typography>
            </Paper>
          ) : (
            filteredDevices.map((device) => (
              <DeviceCard key={device._id} device={device} />
            ))
          )}
          {devicesData && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={devicesData.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Por página:"
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
            />
          )}
        </Box>
      ) : (
        // Vista desktop con tabla
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Dispositivo</TableCell>
                  <TableCell>Cliente / Vehículo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Expiración</TableCell>
                  <TableCell>Última Conexión</TableCell>
                  <TableCell>SIM</TableCell>
                  <TableCell width={60}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !filteredDevices.length ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm || statusFilter !== 'all' ? 'No se encontraron dispositivos' : 'No hay dispositivos registrados'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDevices.map((device) => (
                    <DeviceRow
                      key={device._id}
                      device={device}
                      onView={handleView}
                      onEdit={handleEdit}
                      onViewMap={handleViewMap}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {devicesData && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={devicesData.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
            />
          )}
        </Paper>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add device"
        onClick={handleAddDevice}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default DevicesPage;